export type QueryKeyPart = string | number | boolean | null | undefined;
export type QueryKeyFilters = Record<string, QueryKeyPart>;

export const queryKeys = {
  seller: {
    root: (storeSlug: string) => ["seller", storeSlug] as const,
    session: (storeSlug: string) => ["seller", storeSlug, "session"] as const,
    profile: (storeSlug: string) => ["seller", storeSlug, "profile"] as const,
    store: (storeSlug: string) => ["seller", storeSlug, "store"] as const,
    settings: (storeSlug: string) => ["seller", storeSlug, "settings"] as const,
    storeSettings: (storeSlug: string) =>
      ["seller", storeSlug, "store-settings"] as const,
    storeGovernorates: (storeSlug: string) =>
      ["seller", storeSlug, "governorates"] as const,
    // Only the governorates the store prices — what the promotion targeting
    // picker offers, so a discount can't point at an out-of-zone city.
    shippingGovernorates: (storeSlug: string) =>
      ["seller", storeSlug, "governorates", "shipping-only"] as const,
    areaRatesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "area-rates"] as const,
    areaRates: (storeSlug: string, governorateId: string) =>
      ["seller", storeSlug, "area-rates", governorateId] as const,
    customDomain: (storeSlug: string) =>
      ["seller", storeSlug, "custom-domain"] as const,
    shippingRulesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "shipping-rules"] as const,
    shippingRules: (storeSlug: string) =>
      ["seller", storeSlug, "shipping-rules", "list"] as const,
    storeSubscription: (storeSlug: string) =>
      ["seller", storeSlug, "store-subscription"] as const,
    storeWalletPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "store-wallet"] as const,
    storeWallet: (storeSlug: string) =>
      ["seller", storeSlug, "store-wallet", "overview"] as const,
    storeWalletTransactions: (storeSlug: string) =>
      ["seller", storeSlug, "store-wallet", "transactions"] as const,
    storeWalletTopUpRequests: (storeSlug: string, filters?: QueryKeyFilters) =>
      [
        "seller",
        storeSlug,
        "store-wallet",
        "top-up-requests",
        filters ?? {},
      ] as const,
    storeWalletTopUpRequestProofUrl: (storeSlug: string, paymentId: string) =>
      [
        "seller",
        storeSlug,
        "store-wallet",
        "top-up-requests",
        paymentId,
        "proof-url",
      ] as const,
    categoriesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "categories"] as const,
    categories: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "categories", "list", filters ?? {}] as const,
    productsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "products"] as const,
    products: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "products", "list", filters ?? {}] as const,
    productById: (storeSlug: string, productId: string) =>
      ["seller", storeSlug, "products", productId] as const,
    productVariantCosts: (storeSlug: string, productId: string) =>
      ["seller", storeSlug, "products", productId, "variant-costs"] as const,
    preparedCartsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "prepared-carts"] as const,
    preparedCarts: (storeSlug: string, limit: number, offset: number, generation: number, filters: QueryKeyFilters = {}) =>
      ["seller", storeSlug, "prepared-carts", "list", { ...filters, limit, offset, generation }] as const,
    cartCheckoutOptions: (storeSlug: string, generation: number) =>
      ["seller", storeSlug, "cart-checkout-options", generation] as const,
    cartCheckoutAreas: (storeSlug: string, governorateId: string, generation: number) =>
      ["seller", storeSlug, "cart-checkout-areas", governorateId, generation] as const,
    preparedCart: (storeSlug: string, cartId: string, generation: number) =>
      ["seller", storeSlug, "prepared-carts", "detail", cartId, generation] as const,
    ordersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "orders"] as const,
    sampleOrder: (storeSlug: string) =>
      ["seller", storeSlug, "orders", "sample"] as const,
    orders: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "orders", "list", filters ?? {}] as const,
    orderStatusCounts: (storeSlug: string, filters?: QueryKeyFilters) =>
      filters
        ? (["seller", storeSlug, "orders", "status-counts", filters] as const)
        : (["seller", storeSlug, "orders", "status-counts"] as const),
    ordersSmart: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "orders", "smart", filters ?? {}] as const,
    orderById: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId] as const,
    orderProof: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "proof"] as const,
    orderReturns: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "returns"] as const,
    orderReturnPhotos: (storeSlug: string, orderId: string, returnId: string) =>
      ["seller", storeSlug, "orders", orderId, "returns", returnId, "photos"] as const,
    returnsSummary: (storeSlug: string) =>
      ["seller", storeSlug, "orders", "returns-summary"] as const,
    orderPaymentTransaction: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "payment-transaction"] as const,
    orderShipment: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "shipment"] as const,
    orderShipmentOptions: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "shipment", "options"] as const,
    transactionsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "transactions"] as const,
    transactions: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "transactions", "list", filters ?? {}] as const,
    transactionById: (storeSlug: string, transactionId: string) =>
      ["seller", storeSlug, "transactions", transactionId] as const,
    paymentOptionsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "payment-options"] as const,
    paymentOptions: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "payment-options", "list", filters ?? {}] as const,
    policyTemplates: (storeSlug: string) =>
      ["seller", storeSlug, "policy-templates"] as const,
    bostaConnection: (storeSlug: string) =>
      ["seller", storeSlug, "bosta-connection"] as const,
    jntConnection: (storeSlug: string) =>
      ["seller", storeSlug, "jnt-connection"] as const,
    shipbluConnection: (storeSlug: string) =>
      ["seller", storeSlug, "shipblu-connection"] as const,
    yfsConnection: (storeSlug: string) =>
      ["seller", storeSlug, "yfs-connection"] as const,
    aramexConnection: (storeSlug: string) =>
      ["seller", storeSlug, "aramex-connection"] as const,
    mylerzConnection: (storeSlug: string) =>
      ["seller", storeSlug, "mylerz-connection"] as const,
    speedafConnection: (storeSlug: string) =>
      ["seller", storeSlug, "speedaf-connection"] as const,
    paymentGatewayConnection: (storeSlug: string, provider: string) =>
      ["seller", storeSlug, "payment-gateway", provider] as const,
    reviewsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "reviews"] as const,
    reviews: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "reviews", "list", filters ?? {}] as const,
    customersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "customers"] as const,
    customers: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "customers", "list", filters ?? {}] as const,
    customerDetail: (storeSlug: string, customerId: string) =>
      ["seller", storeSlug, "customers", "detail", customerId] as const,
    couponsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "coupons"] as const,
    coupons: (storeSlug: string) =>
      ["seller", storeSlug, "coupons", "list"] as const,
    rolesPrefix: (storeSlug: string) => ["seller", storeSlug, "roles"] as const,
    roles: (storeSlug: string) =>
      ["seller", storeSlug, "roles", "list"] as const,
    rolePermissions: (storeSlug: string) =>
      ["seller", storeSlug, "roles", "permissions"] as const,
    membersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "members"] as const,
    members: (storeSlug: string) =>
      ["seller", storeSlug, "members", "list"] as const,
    invitesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "invites"] as const,
    invites: (storeSlug: string) =>
      ["seller", storeSlug, "invites", "list"] as const,
    search: (storeSlug: string, q: string) =>
      ["seller", storeSlug, "search", q] as const,
    dashboardAnalyticsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "dashboard-analytics"] as const,
    /**
     * `since` is part of the key, not an incidental param: the query uses
     * `keepPreviousData`, so without it a refetch on a new window would keep
     * showing the PREVIOUS window's overnight numbers — the exact class of
     * silently-wrong answer the morning board exists to stop.
     */
    dashboardAnalytics: (
      storeSlug: string,
      range: string,
      from?: string | null,
      to?: string | null,
      since?: string | null,
    ) =>
      [
        "seller",
        storeSlug,
        "dashboard-analytics",
        range,
        from ?? "",
        to ?? "",
        since ?? "",
      ] as const,
  },
} as const;
